/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Heading, EmailField, Button, DividerNew, Icon } from 'sarsaparilla';

import { useRecaptchaWithFallback, RecaptchaCheckbox } from 'shared-ui/recaptcha';
import { postRequestPasswordReset } from 'shared-ui/api/account';
import { ProtectedByRecaptcha } from '../components/ProtectedByRecaptcha';
import {
    RequestPasswordResetErrors,
    type RequestPasswordResetErrorType,
} from './RequestPasswordResetErrors';

interface RequestPasswordResetFormProps {
    onEmailSent: (email: string) => void;
    onLogInClick: () => void;
}

export function RequestPasswordResetForm({
    onEmailSent,
    onLogInClick,
}: RequestPasswordResetFormProps) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorType, setErrorType] = React.useState<RequestPasswordResetErrorType>(null);

    const emailFieldRef = React.useRef<EmailField>(null);
    const emailInnerRef = React.useRef<HTMLInputElement>(null);

    const {
        getToken,
        getRecaptchaRegion,
        handleRecaptchaError,
        isV2Invalid,
        recaptchaCheckboxProps,
    } = useRecaptchaWithFallback();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorType(null);

        // Get form data
        const formData = new FormData(event.currentTarget);
        const email = formData.get('email')?.toString().trim();

        // Validate form
        const validation = emailFieldRef.current?.validate();

        if (!email || validation?.isInvalid || isV2Invalid) {
            emailInnerRef.current?.focus();
            setIsLoading(false);
            return;
        }

        // Send request to reset password
        try {
            // Get Recaptcha type and token
            const recaptchaToken = await getToken('passwordreset');
            const recaptchaRegion = getRecaptchaRegion();

            // Extra check to make sure we have a token value
            if (!recaptchaToken) {
                setErrorType('recaptchaFailed');
                setIsLoading(false);
                return;
            }

            await postRequestPasswordReset({ email, recaptchaToken, recaptchaRegion });

            // set parent state to move to next step
            onEmailSent(email);
        } catch (error) {
            const { shouldRetry, isFailed } = handleRecaptchaError(error);

            if (shouldRetry) {
                setErrorType('additionalChallenge');
            } else if (isFailed) {
                setErrorType('recaptchaFailed');
            } else {
                setErrorType('unknown');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="px-sm-3 pt-sm-3 pb-sm-1">
            <Heading headingLevel={1} appearance="h4" className="mb-1" id="modal-heading">
                Reset Password
            </Heading>

            <p className="text-md pt-half">
                Enter the email address associated with your account and we‘ll send a code
                to reset your password.
            </p>

            <form className="my-4" onSubmit={handleSubmit}>
                <RequestPasswordResetErrors errorType={errorType} className="mb-4" />

                <EmailField
                    label="Email Address"
                    type="email"
                    id="email"
                    name="email"
                    isRequired
                    ref={emailFieldRef}
                    innerRef={emailInnerRef}
                />

                <RecaptchaCheckbox
                    {...recaptchaCheckboxProps}
                    isFallbackOnly
                    action="passwordreset"
                    className="mt-3"
                />

                <Button
                    type="submit"
                    size="lg"
                    appearance="primary"
                    shouldFitContainer
                    className="mt-4"
                    isLoading={isLoading}
                    gaTrackingId="440238972040"
                >
                    Send Verification Code
                </Button>

                <Button
                    type="button"
                    appearance="subtle"
                    shouldFitContainer
                    className="mt-1"
                    onClick={onLogInClick}
                    iconBeforeElement={<Icon iconName="chevron-left" />}
                >
                    Back to Log In
                </Button>
            </form>

            <DividerNew className="mb-2" />

            <ProtectedByRecaptcha />
        </div>
    );
}

// cSpell:ignore passwordreset
